import { STRINGS } from '@/constants/strings';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { LEVEL_DETAIL_SKILL_CONTENT } from '@/features/guardian-player/components/level-detail/skill-content';
import type {
  GuardianPlayerLevelDetailAttempt,
  GuardianPlayerLevelDetailData,
  GuardianPlayerLevelDetailPin,
  GuardianPlayerLevelDetailPinKey,
  GuardianPlayerLevelDetailStatus,
} from '@/features/guardian-player/types/level-detail';
import { buildPinUrl } from '@/features/player-session/utils';
import {
  badgeAggregateStatusFromCheckpoints,
  levelAggregateStatusFromBadges,
  type LevelStatusKey,
} from '@/features/player/components/mutate-player/mutate-level-status';
import {
  formatDateEarnedShort,
  isCheckpointStatusCompleted,
  parseLevelNumber,
  parseProgressDateStringToDate,
  sortProgressCheckpoints,
} from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import { DATE_TEXT_LOCALE_OPTIONS } from '@/utils/date-formats';
import type {
  PlayerProgressBadge,
  PlayerProgressCheckpoint,
  PlayerProgressItem,
} from '@/features/player/components/types';
import {
  formatProgressBadgeTitle,
  formatProgressCheckpointTitleOverview,
} from '@/features/player/utils/progress-title';
import { buildGuardianPinPicture } from './pin-image';

type GuardianPlayerLevelDetailSource = Pick<
  GuardianPlayer,
  'firstname' | 'id' | 'lastname' | 'progress'
>;

const PIN_KEY_BY_BADGE_NAME: Array<[GuardianPlayerLevelDetailPinKey, RegExp]> = [
  ['putting', /putting/i],
  ['aroundTheGreen', /around\s*the\s*green|atg|chipping|pitching|sand/i],
  ['fullSwing', /full\s*swing|fullswing|driver|iron/i],
  ['scoring', /scoring|score|mastery/i],
  ['knowledge', /knowledge|etiquette|equipment|rules|terminology/i],
];

const PIN_DISPLAY_ORDER: Record<GuardianPlayerLevelDetailPinKey, number> = {
  putting: 0,
  aroundTheGreen: 1,
  fullSwing: 2,
  scoring: 3,
  knowledge: 4,
};

function getPinKeyFromBadgeName(name: string): GuardianPlayerLevelDetailPinKey {
  return PIN_KEY_BY_BADGE_NAME.find(([, pattern]) => pattern.test(name))?.[0] ?? 'knowledge';
}

function mapLevelRowStatus(status: LevelStatusKey): GuardianPlayerLevelDetailStatus {
  switch (status) {
    case 'completed':
      return 'earned';
    case 'partial':
      return 'working';
    case 'incomplete':
    default:
      return 'notStarted';
  }
}

function formatShortDisplayDate(raw: unknown) {
  const parsed = parseProgressDateStringToDate(String(raw ?? ''));
  if (parsed) {
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    const year = String(parsed.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  }

  const fallback = formatDateEarnedShort(String(raw ?? ''));
  return fallback || String(raw ?? '').trim();
}

function formatLongDisplayDate(raw: unknown) {
  const parsed = parseProgressDateStringToDate(String(raw ?? ''));
  if (parsed) {
    return parsed.toLocaleDateString('en-US', DATE_TEXT_LOCALE_OPTIONS);
  }

  const short = formatDateEarnedShort(String(raw ?? ''));
  if (!short) return undefined;
  const [month, day, year] = short.split('/');
  if (!month || !day || !year) return short;
  const date = new Date(Number(`20${year}`), Number(month) - 1, Number(day));
  if (Number.isNaN(date.getTime())) return short;
  return date.toLocaleDateString('en-US', DATE_TEXT_LOCALE_OPTIONS);
}

export function buildAttemptsFromCheckpoints(
  badge: PlayerProgressBadge,
): GuardianPlayerLevelDetailAttempt[] {
  const attempts: GuardianPlayerLevelDetailAttempt[] = [];
  const checkpoints = sortProgressCheckpoints(badge.checkpoints);

  checkpoints.forEach((checkpoint: PlayerProgressCheckpoint, checkpointIndex) => {
    const rawAttempts = Array.isArray(checkpoint.attempts) ? checkpoint.attempts : [];
    const label = formatProgressCheckpointTitleOverview(checkpoint.name, {
      layerSeparator: ': ',
    });
    const isCompleted = isCheckpointStatusCompleted(checkpoint.status);

    if (rawAttempts.length === 0) {
      attempts.push({
        id: `${badge.name}-${checkpoint.name}`,
        label,
        completed: isCompleted,
        groupStart: true,
      });
      return;
    }

    const completionIndex = isCompleted ? rawAttempts.length - 1 : -1;

    rawAttempts.forEach((rawAttempt, attemptIndex) => {
      const isCompletionAttempt = attemptIndex === completionIndex;
      attempts.push({
        id: `${badge.name}-${checkpoint.name}-${attemptIndex}`,
        label,
        completed: isCompletionAttempt,
        completedOn: isCompletionAttempt ? formatShortDisplayDate(rawAttempt) : undefined,
        incompleteOn: isCompletionAttempt ? undefined : formatShortDisplayDate(rawAttempt),
        groupStart: attemptIndex === 0 || (checkpointIndex > 0 && attemptIndex === 0),
      });
    });
  });

  return attempts;
}

function buildPinFromBadge(
  badge: PlayerProgressBadge,
  index: number,
  levelNumber: number,
): GuardianPlayerLevelDetailPin {
  const key = getPinKeyFromBadgeName(badge.name);
  const status = mapLevelRowStatus(badgeAggregateStatusFromCheckpoints(badge));
  const pinPicture = buildGuardianPinPicture(levelNumber, badge.name, status);
  const content = LEVEL_DETAIL_SKILL_CONTENT[levelNumber]?.[key];

  return {
    key,
    title: formatProgressBadgeTitle(badge.name),
    status,
    pinPicture,
    pinImageUrl: buildPinUrl(pinPicture),
    date: status === 'earned' ? formatLongDisplayDate(badge.dateEarned) : undefined,
    whatItTests: content?.whatItTests,
    criteria: content?.criteria,
    attempts: buildAttemptsFromCheckpoints(badge),
    sourceIndex: index,
  };
}

function getLevelProgress(
  progress: PlayerProgressItem[] | undefined,
  level: number,
): PlayerProgressItem | undefined {
  return progress?.find(item => parseLevelNumber(item.name) === level);
}

function getLevelProgressPercent(levelProgress: PlayerProgressItem | undefined) {
  const badges = levelProgress?.badges ?? [];
  if (badges.length === 0) return 0;
  const earnedCount = badges.filter(
    badge => badgeAggregateStatusFromCheckpoints(badge) === 'completed',
  ).length;
  return Math.round((earnedCount / badges.length) * 100);
}

function getLevelStatusLabel(levelProgress: PlayerProgressItem | undefined) {
  return levelAggregateStatusFromBadges(levelProgress) === 'completed'
    ? STRINGS.guardianPlayer.levelDetail.levelStatus.completed
    : STRINGS.guardianPlayer.levelDetail.levelStatus.inProgress;
}

function getSelectedPinId(pins: GuardianPlayerLevelDetailPin[]) {
  return pins[0]?.sourceIndex ?? 0;
}

function sortPinsByDesignOrder(pins: GuardianPlayerLevelDetailPin[]) {
  return [...pins].sort((a, b) => {
    const orderDiff = PIN_DISPLAY_ORDER[a.key] - PIN_DISPLAY_ORDER[b.key];
    if (orderDiff !== 0) return orderDiff;
    return a.sourceIndex - b.sourceIndex;
  });
}

export function buildGuardianPlayerLevelDetailData(
  player: GuardianPlayerLevelDetailSource,
  level: number,
): GuardianPlayerLevelDetailData {
  const levelProgress = getLevelProgress(player.progress, level);
  const pins = sortPinsByDesignOrder(
    (levelProgress?.badges ?? []).map((badge, index) => buildPinFromBadge(badge, index, level)),
  );

  return {
    level,
    progressPercent: getLevelProgressPercent(levelProgress),
    levelStatusLabel: getLevelStatusLabel(levelProgress),
    selectedPinId: getSelectedPinId(pins),
    playerId: player.id,
    playerName: [player.firstname, player.lastname].filter(Boolean).join(' ').trim(),
    pins,
  };
}
